import { inject, Injectable, signal } from '@angular/core';
import { TrackData } from '@shared/interfacres/track';
import { firstValueFrom } from 'rxjs';
import { TracksManagerService } from '@data-access/manager/services/tracks-manager.service';
import { LibraryStore } from '@data-access/user/stores/library.store';
import { PlaylistsStore } from '@data-access/user/stores/playlists.store';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import { Platform } from '@angular/cdk/platform';


export interface CurrentTrack {
  id: number;
  name: string;
  author: string;
  url: string;
  duration?: number;
  waveform: number[][] | undefined;
}

@Injectable({providedIn: 'root'})
export class PlayerSourceService {
  platform = inject(Platform);

  tracksService = inject(TracksManagerService);
  libraryStore = inject(LibraryStore);
  playlistsStore = inject(PlaylistsStore);
  isAdmin = false;

  tracksList: Partial<TrackData>[] = [];

  // eventBus = new Subject<string>();

  isPlaying = signal(false);

  currentTrack = signal<CurrentTrack | null>(null);

  nearTracksData = signal({
    previous: false,
    next: false
  });

  ffmpeg;
  isMetaCleaningEnabled = false;
  isSafari = false;

  constructor() {
    this.ffmpeg = new FFmpeg();

    // if (this.platform.SAFARI) {
    //   this.isSafari = true;
    //   this.initFFmpeg();
    // }

  }

  async initFFmpeg() {
    this.ffmpeg.on('log', ({message}) => {
      console.log('FFmpeg LOG', message);
    });
    await this.ffmpeg.load({
      coreURL: await toBlobURL(`/assets/workers/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm`, 'application/wasm'),
      classWorkerURL: '/assets/workers/worker.js'
    });
    console.log('FFmpeg WORKER initiated');
    this.isMetaCleaningEnabled = true;
  }

  play() {
    this.isPlaying.set(true);
  }
  pause() {
    this.isPlaying.set(false);
  }

  init(isAdmin = false) {
    this.isAdmin = isAdmin;
  }

  async setTracksList(tracksList: Partial<TrackData>[], index = 0, force = false) {
    this.tracksList = tracksList;

    const track = await this.initTrack(tracksList[index]);


    if (!track) {
      return;
    }
    this.currentTrack.set(track);
    this.setNearestData(index);
    if (force) {
      this.play();
    }
  }

  setNearestData(index: number) {
    const previous = index > 0;
    const next = index + 1 < this.tracksList.length;

    this.nearTracksData.set({
      previous,
      next
    })

  }



  async initTrack(track: Partial<TrackData>) {

    if (!track.id) {
      console.error('tack id is missing', track);
      return;
    }

    if (!this.isAdmin) {
      firstValueFrom(this.libraryStore.markTrackAsListened(track.id));
      this.libraryStore.markTopTrackAsListened(track.id);
      this.playlistsStore.markAsListened(track.id);
    }

    const { url, waveform } = await firstValueFrom(this.tracksService.getSignedTrackUrlById(track.id));

    return  {
      name: track.title,
      author: track.artists || '',
      url,
      waveform,
      duration: track.duration,
      id: track.id
    } as CurrentTrack;
  }

  clear() {
    this.pause();
    this.nearTracksData.set({
      previous: false,
      next: false
    });
    this.isAdmin = false;
    this.currentTrack.set(null);
  }

  async nextTrack() {
    const currentTrackIndex = this.tracksList.findIndex((track: Partial<TrackData>) => {
      return track.id === this.currentTrack()?.id;
    })

    if (currentTrackIndex >= 0 && currentTrackIndex < this.tracksList.length) {
      const track = await this.initTrack(this.tracksList[currentTrackIndex + 1]);


      if (!track) {
        return;
      }
      this.currentTrack.set(track);
      this.setNearestData(currentTrackIndex + 1);
      if (this.isPlaying()) {
        this.play();
      }
    }
  }

    async prevTrack() {
      const currentTrackIndex = this.tracksList.findIndex((track: Partial<TrackData>) => {
        return track.id === this.currentTrack()?.id;
      })

      if (currentTrackIndex >= 0 && currentTrackIndex  > 0) {
        const track = await this.initTrack(this.tracksList[currentTrackIndex - 1]);

        if (!track) {
          return;
        }

        this.currentTrack.set(track);
        this.setNearestData(currentTrackIndex - 1);
        if (this.isPlaying()) {
          this.play();
        }
      }
    }
}
